import moment from 'moment';
import SITE_SETTINGS from 'src/constants/whiteLabel';
import { USER_LEVEL_ACCESS } from 'src/components/access';
import { IStore } from 'src/interfaces/store';
import { IUser } from 'src/interfaces/user';
import { ILead } from 'src/interfaces/lead';
import { handleGetArea } from 'src/modules/calls/helpers/handleGetArea';
import { THandleGetArea } from 'src/modules/calls/helpers/types';
import { handleGetPhoneAgencies } from 'src/modules/store/helpers';
import { formatPhoneByCountryCode } from 'src/modules/phones/utils/formatPhoneByCountryCode';
import { getCountryCode } from 'src/utils/format/getCountryCode';
import { getDocumentId } from 'src/utils/mongo/getDocumentId';
import { MakeCallHandle } from 'src/shared/communications/call/makeCall';
import {
    CALL_COMMENT_ACTION,
    CALL_FOLLOW_UP_DELAY_MINUTES,
} from 'src/modules/assistant/constants/actions';
import { createActionHandler } from 'src/modules/assistant/actions/createActionHandler';
import { TActionDraft } from 'src/modules/assistant/types/message';

const LEGACY_MEXICO_MOBILE_PREFIX = /\+521/g;
const MEXICO_PREFIX = '+52';
const CALL_TYPE = 'follow_call';
const CALL_CHANNEL = 'dpx';
const ABORT_EVENT = 'abort';
const RECORDING_MODE = Object.freeze({
    ENABLED: 'record-from-answer',
    DISABLED: 'do-not-record',
});

interface TAgencyPhone {
    phone?: string;
    callerId?: string;
}

const normalizePhone = (phone?: string) =>
    String(phone || '').replace(LEGACY_MEXICO_MOBILE_PREFIX, MEXICO_PREFIX);

const getCallerPhone = (
    user: IUser,
    lead: ILead,
    agencyPhone: TAgencyPhone
) => {
    if (user.isVerifiedCallerId) return user.phone;
    if (lead.agent?.isVerifiedCallerId) return lead.agent.phone;
    return agencyPhone.callerId || agencyPhone.phone;
};

export const call = createActionHandler<TActionDraft>({
    cancellable: true,
    validate: () => true,
    execute: async (
        _,
        { lead, user, t, leadActions, makeCall, callToken, signal }
    ) => {
        const store = lead.store as Partial<IStore>;
        const storeId = getDocumentId(lead.store);
        const token = callToken || (await leadActions.generateToken(storeId));
        if (!token || !lead.phone || !store?.areas) return false;

        const area = handleGetArea({
            type: lead.type,
            // Leads of both car types fall back to the lead type, as before
            carType: lead.carType as THandleGetArea['carType'],
        });
        const agencyPhone = handleGetPhoneAgencies({
            area,
            areas: store.areas,
        });
        if (!agencyPhone.success) return false;

        const callerPhone = normalizePhone(
            getCallerPhone(user, lead, agencyPhone)
        );
        const from = formatPhoneByCountryCode(
            callerPhone,
            getCountryCode(callerPhone),
            getCountryCode(store.countryCode)
        );
        const to = formatPhoneByCountryCode(
            lead.phone,
            lead.countryCode,
            store.countryCode
        );
        const assignedBy = user.accessLevel !== USER_LEVEL_ACCESS ? user : null;

        return new Promise<boolean>((resolve) => {
            let accepted = false;
            let finished = false;
            let handle: MakeCallHandle | null = null;
            const hangUp = () => handle?.disconnect();
            const finish = () => {
                if (finished) return;
                finished = true;
                signal?.removeEventListener(ABORT_EVENT, hangUp);
                if (accepted)
                    leadActions.addComment({
                        pending: true,
                        action: [CALL_COMMENT_ACTION],
                        reschedule: moment()
                            .add(CALL_FOLLOW_UP_DELAY_MINUTES, 'minutes')
                            .format(),
                        comment: t('Assistant.CallCommentGenerated', {
                            name: lead.name,
                        }),
                        user: lead.agent || user,
                        isAssistantGenerated: true,
                        createdAt: Date.now(),
                        ...(assignedBy && { assignedBy }),
                    });
                resolve(accepted);
            };

            signal?.addEventListener(ABORT_EVENT, hangUp);
            makeCall({
                token,
                to,
                from,
                params: {
                    user: user._id,
                    from,
                    callerId: from,
                    area,
                    To: to,
                    lead: lead._id,
                    typeCall: CALL_TYPE,
                    store: storeId,
                    client: SITE_SETTINGS.client,
                    record: store.isRecording
                        ? RECORDING_MODE.ENABLED
                        : RECORDING_MODE.DISABLED,
                    assignedBy: assignedBy?._id || null,
                    type: CALL_CHANNEL,
                },
                onTokenWillExpire: () => {
                    leadActions.generateToken(storeId);
                },
                onAccept: () => {
                    accepted = true;
                },
                onDisconnect: finish,
                onError: () => {
                    if (!accepted) finish();
                },
            })
                .then((callHandle) => {
                    handle = callHandle;
                    if (signal?.aborted) hangUp();
                })
                .catch(finish);
        });
    },
});
